import React, { useState, useEffect } from 'react';
import { 
  ExclamationTriangleIcon,
  PhoneIcon,
  ClockIcon,
  UserIcon,
  HeartIcon,
  TruckIcon,
  CheckCircleIcon,
  XMarkIcon
} from '@heroicons/react/24/outline';
import { emergencyAPI, patientAPI } from '../../api';

const Emergency = () => {
  const [emergencies, setEmergencies] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [newEmergency, setNewEmergency] = useState({
    patientName: '',
    age: '',
    gender: 'male',
    contactNumber: '',
    emergencyType: 'accident',
    severity: 'moderate',
    chiefComplaint: '',
    vitals: {
      bloodPressure: '',
      pulse: '',
      temperature: '',
      respiration: ''
    },
    arrivalMode: 'walk-in',
    attendingPhysician: '',
    notes: ''
  });

  useEffect(() => {
    loadEmergencies();
  }, []);

  const loadEmergencies = async () => {
    try {
      setLoading(true);
      const response = await emergencyAPI.getEmergencies();
      setEmergencies(response.data?.emergencies || []);
    } catch (error) {
      console.error('Error loading emergencies:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleCreateEmergency = async (e) => {
    e.preventDefault();
    try {
      await emergencyAPI.createEmergency(newEmergency);
      setShowCreateForm(false);
      setNewEmergency({
        patientName: '',
        age: '',
        gender: 'male',
        contactNumber: '',
        emergencyType: 'accident',
        severity: 'moderate',
        chiefComplaint: '',
        vitals: {
          bloodPressure: '',
          pulse: '',
          temperature: '',
          respiration: ''
        },
        arrivalMode: 'walk-in',
        attendingPhysician: '',
        notes: ''
      });
      loadEmergencies();
      alert('Emergency case created successfully!');
    } catch (error) {
      console.error('Error creating emergency:', error);
      alert('Error creating emergency case. Please try again.');
    }
  };

  const handleUpdateStatus = async (emergencyId, status) => {
    try {
      await emergencyAPI.updateEmergency(emergencyId, { status });
      loadEmergencies();
    } catch (error) {
      console.error('Error updating emergency status:', error);
      alert('Error updating status. Please try again.');
    }
  };

  const getSeverityColor = (severity) => {
    switch (severity) {
      case 'critical': return 'bg-red-100 text-red-800 border-red-200';
      case 'high': return 'bg-orange-100 text-orange-800 border-orange-200';
      case 'moderate': return 'bg-yellow-100 text-yellow-800 border-yellow-200';
      case 'low': return 'bg-green-100 text-green-800 border-green-200';
      default: return 'bg-gray-100 text-gray-800 border-gray-200';
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'active': return 'bg-blue-100 text-blue-800';
      case 'in-treatment': return 'bg-purple-100 text-purple-800';
      case 'stable': return 'bg-green-100 text-green-800';
      case 'discharged': return 'bg-gray-100 text-gray-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const EmergencyCard = ({ emergency }) => (
    <div className={`
      bg-white rounded-lg shadow-md border-l-4 p-6 mb-4
      ${emergency.severity === 'critical' ? 'border-l-red-500' :
        emergency.severity === 'high' ? 'border-l-orange-500' :
        emergency.severity === 'moderate' ? 'border-l-yellow-500' :
        'border-l-green-500'
      }
    `}>
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center">
          <div className="w-10 h-10 bg-red-100 rounded-full flex items-center justify-center mr-3">
            <ExclamationTriangleIcon className="w-5 h-5 text-red-600" />
          </div>
          <div>
            <h3 className="text-lg font-semibold text-gray-900">{emergency.patientName}</h3>
            <p className="text-sm text-gray-500">
              {emergency.age} years, {emergency.gender} • {emergency.contactNumber}
            </p>
          </div>
        </div>
        <div className="flex items-center space-x-2">
          <span className={`px-3 py-1 text-xs font-medium rounded-full border ${getSeverityColor(emergency.severity)}`}>
            {emergency.severity.toUpperCase()}
          </span>
          <span className={`px-3 py-1 text-xs font-medium rounded-full ${getStatusColor(emergency.status)}`}>
            {emergency.status?.replace('-', ' ').toUpperCase()}
          </span>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
        <div>
          <p className="text-sm text-gray-600">Emergency Type</p>
          <p className="font-medium capitalize">{emergency.emergencyType}</p>
        </div>
        <div>
          <p className="text-sm text-gray-600">Arrival Mode</p>
          <p className="font-medium capitalize">{emergency.arrivalMode}</p>
        </div>
        <div>
          <p className="text-sm text-gray-600">Chief Complaint</p>
          <p className="font-medium">{emergency.chiefComplaint}</p>
        </div>
        <div>
          <p className="text-sm text-gray-600">Attending Physician</p>
          <p className="font-medium">{emergency.attendingPhysician || 'Not assigned'}</p>
        </div>
      </div>

      {emergency.vitals && (
        <div className="bg-gray-50 rounded-lg p-4 mb-4">
          <h4 className="text-sm font-semibold text-gray-900 mb-2">Vital Signs</h4>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
            <div>
              <span className="text-gray-600">BP:</span>
              <span className="ml-1 font-medium">{emergency.vitals.bloodPressure || 'N/A'}</span>
            </div>
            <div>
              <span className="text-gray-600">Pulse:</span>
              <span className="ml-1 font-medium">{emergency.vitals.pulse || 'N/A'}</span>
            </div>
            <div>
              <span className="text-gray-600">Temp:</span>
              <span className="ml-1 font-medium">{emergency.vitals.temperature || 'N/A'}</span>
            </div>
            <div>
              <span className="text-gray-600">Resp:</span>
              <span className="ml-1 font-medium">{emergency.vitals.respiration || 'N/A'}</span>
            </div>
          </div>
        </div>
      )}

      <div className="flex items-center justify-between">
        <div className="flex items-center text-sm text-gray-500">
          <ClockIcon className="w-4 h-4 mr-1" />
          Arrived: {new Date(emergency.createdAt).toLocaleString()}
        </div>
        <div className="flex space-x-2">
          {emergency.status !== 'discharged' && (
            <>
              <button
                onClick={() => handleUpdateStatus(emergency._id, 'in-treatment')}
                className="bg-purple-600 hover:bg-purple-700 text-white px-3 py-1 rounded text-sm"
              >
                Start Treatment
              </button>
              <button
                onClick={() => handleUpdateStatus(emergency._id, 'stable')}
                className="bg-green-600 hover:bg-green-700 text-white px-3 py-1 rounded text-sm"
              >
                Mark Stable
              </button>
              <button
                onClick={() => handleUpdateStatus(emergency._id, 'discharged')}
                className="bg-gray-600 hover:bg-gray-700 text-white px-3 py-1 rounded text-sm"
              >
                Discharge
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Emergency Department</h1>
          <p className="text-gray-600">Manage emergency cases and critical patients</p>
        </div>
        <div className="flex items-center space-x-3">
          <div className="bg-red-100 rounded-lg px-4 py-2">
            <div className="flex items-center">
              <ExclamationTriangleIcon className="w-5 h-5 text-red-600 mr-2" />
              <span className="text-sm font-medium text-red-800">
                {emergencies.filter(e => e.status === 'active').length} Active Cases
              </span>
            </div>
          </div>
          <button
            onClick={() => setShowCreateForm(true)}
            className="bg-red-600 hover:bg-red-700 text-white font-semibold py-2 px-4 rounded-lg flex items-center"
          >
            <ExclamationTriangleIcon className="w-5 h-5 mr-2" />
            New Emergency
          </button>
        </div>
      </div>

      {/* Quick Stats */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
        <div className="bg-white rounded-lg shadow-md border border-red-200 p-6">
          <div className="flex items-center">
            <div className="w-12 h-12 bg-red-100 rounded-lg flex items-center justify-center">
              <ExclamationTriangleIcon className="w-6 h-6 text-red-600" />
            </div>
            <div className="ml-4">
              <h3 className="text-2xl font-bold text-gray-900">
                {emergencies.filter(e => e.severity === 'critical').length}
              </h3>
              <p className="text-sm font-medium text-gray-600">Critical Cases</p>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow-md border border-orange-200 p-6">
          <div className="flex items-center">
            <div className="w-12 h-12 bg-orange-100 rounded-lg flex items-center justify-center">
              <HeartIcon className="w-6 h-6 text-orange-600" />
            </div>
            <div className="ml-4">
              <h3 className="text-2xl font-bold text-gray-900">
                {emergencies.filter(e => e.status === 'in-treatment').length}
              </h3>
              <p className="text-sm font-medium text-gray-600">In Treatment</p>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow-md border border-green-200 p-6">
          <div className="flex items-center">
            <div className="w-12 h-12 bg-green-100 rounded-lg flex items-center justify-center">
              <CheckCircleIcon className="w-6 h-6 text-green-600" />
            </div>
            <div className="ml-4">
              <h3 className="text-2xl font-bold text-gray-900">
                {emergencies.filter(e => e.status === 'stable').length}
              </h3>
              <p className="text-sm font-medium text-gray-600">Stable</p>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow-md border border-blue-200 p-6">
          <div className="flex items-center">
            <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center">
              <TruckIcon className="w-6 h-6 text-blue-600" />
            </div>
            <div className="ml-4">
              <h3 className="text-2xl font-bold text-gray-900">
                {emergencies.filter(e => e.arrivalMode === 'ambulance').length}
              </h3>
              <p className="text-sm font-medium text-gray-600">By Ambulance</p>
            </div>
          </div>
        </div>
      </div>

      {/* Emergency Cases */}
      <div className="bg-white rounded-lg shadow-md border border-gray-200 p-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-6">Active Emergency Cases</h3>
        
        {loading ? (
          <div className="text-center py-8">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-red-600 mx-auto"></div>
            <p className="text-gray-500 mt-4">Loading emergencies...</p>
          </div>
        ) : emergencies.length > 0 ? (
          <div>
            {emergencies.map((emergency) => (
              <EmergencyCard key={emergency._id} emergency={emergency} />
            ))}
          </div>
        ) : (
          <div className="text-center py-8">
            <ExclamationTriangleIcon className="w-12 h-12 text-gray-400 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-gray-900 mb-2">No Emergency Cases</h3>
            <p className="text-gray-500">No active emergency cases at the moment.</p>
          </div>
        )}
      </div>

      {/* Create Emergency Modal */}
      {showCreateForm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full mx-4 max-h-screen overflow-y-auto">
            <div className="p-6">
              <div className="flex items-center justify-between mb-6">
                <h3 className="text-xl font-semibold text-gray-900">New Emergency Case</h3>
                <button
                  onClick={() => setShowCreateForm(false)}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <XMarkIcon className="w-6 h-6" />
                </button>
              </div>

              <form onSubmit={handleCreateEmergency}>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Patient Name</label>
                    <input
                      type="text"
                      required
                      value={newEmergency.patientName}
                      onChange={(e) => setNewEmergency({...newEmergency, patientName: e.target.value})}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Age</label>
                    <input
                      type="number"
                      required
                      value={newEmergency.age}
                      onChange={(e) => setNewEmergency({...newEmergency, age: e.target.value})}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Gender</label>
                    <select
                      value={newEmergency.gender}
                      onChange={(e) => setNewEmergency({...newEmergency, gender: e.target.value})}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500"
                    >
                      <option value="male">Male</option>
                      <option value="female">Female</option>
                      <option value="other">Other</option>
                    </select>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Contact Number</label>
                    <input
                      type="tel"
                      required
                      value={newEmergency.contactNumber}
                      onChange={(e) => setNewEmergency({...newEmergency, contactNumber: e.target.value})}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Emergency Type</label>
                    <select
                      value={newEmergency.emergencyType}
                      onChange={(e) => setNewEmergency({...newEmergency, emergencyType: e.target.value})}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500"
                    >
                      <option value="accident">Accident</option>
                      <option value="cardiac">Cardiac Emergency</option>
                      <option value="respiratory">Respiratory Distress</option>
                      <option value="trauma">Trauma</option>
                      <option value="poisoning">Poisoning</option>
                      <option value="burns">Burns</option>
                      <option value="other">Other</option>
                    </select>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Severity</label>
                    <select
                      value={newEmergency.severity}
                      onChange={(e) => setNewEmergency({...newEmergency, severity: e.target.value})}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500"
                    >
                      <option value="low">Low</option>
                      <option value="moderate">Moderate</option>
                      <option value="high">High</option>
                      <option value="critical">Critical</option>
                    </select>
                  </div>
                </div>

                <div className="mt-6">
                  <label className="block text-sm font-medium text-gray-700 mb-2">Chief Complaint</label>
                  <textarea
                    required
                    value={newEmergency.chiefComplaint}
                    onChange={(e) => setNewEmergency({...newEmergency, chiefComplaint: e.target.value})}
                    rows={3}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500"
                    placeholder="Describe the main complaint or symptoms..."
                  />
                </div>

                <div className="mt-6 flex justify-end space-x-3">
                  <button
                    type="button"
                    onClick={() => setShowCreateForm(false)}
                    className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    className="px-4 py-2 bg-red-600 hover:bg-red-700 text-white rounded-lg"
                  >
                    Create Emergency Case
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Emergency;